/**
 * @file Builds the Swagger specification URL and metadata for a token.
 */
import { HOST } from "@components/Constants";

const HTTPS_PREFIX = "https://";
const SPEC_DIRECTORY = "/static/specs";

const SPEC_CONSOLE = "console.swagger.json";
const SPEC_BACKSTAGE = "backstage.swagger.json";

const TITLE_CONSOLE = "Console API";
const TITLE_BACKSTAGE = "Back Office API";

export type Specification = {
  url: string;
  host: string;
  title: string;
  fileName: string;
  path: string;
};

type Props = {
  domain?: string;
  isConsoleOperator: boolean;
};

// NOTE: an operator of the back office calls the API on the domain of the
//       organization. An organization with no domain of its own uses the
//       host of this app, and the console API is at the host of this app
//       each time, because the console holds more than one organization.
/**
 * Finds the host that the client calls, from the organization domain.
 */
const getClientHost = (domain?: string): string => {
  const hasDomain = !!domain;

  if (!hasDomain) {
    return HOST;
  }

  const host = `${HTTPS_PREFIX}${domain}`;

  return host;
};

/**
 * Builds the specification URL, host and title for the console or Backstage API.
 */
const getSpecification = ({
  domain,
  isConsoleOperator,
}: Props): Specification => {
  const fileName = isConsoleOperator
    ? SPEC_CONSOLE
    : SPEC_BACKSTAGE;

  const title = isConsoleOperator
    ? TITLE_CONSOLE
    : TITLE_BACKSTAGE;

  const host = isConsoleOperator
    ? HOST
    : getClientHost(domain);

  const specificationPath = `${SPEC_DIRECTORY}/${fileName}`;
  const url = `${host}${specificationPath}`;

  const specification = {
    url,
    host,
    title,
    fileName,
    path: specificationPath,
  };

  return specification;
};

export default getSpecification;
